// frontend/src/pages/RegistrationSuccess.jsx
import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function RegistrationSuccess() {
    const navigate = useNavigate();
    const location = useLocation();
    const [showTour, setShowTour] = useState(true);
    const [isVisible, setIsVisible] = useState(false);

    // Get user data from location state (passed from Register/Login)
    const userData = location.state?.userData || {};
    const firstName = userData.first_name || 'User';
    const isNewUser = location.state?.isNewUser ?? true; // Default to true for backward compatibility

    // Determine the greeting based on whether user is new or existing
    const greeting = isNewUser ? `Welcome, ${firstName}! 👋` : `Welcome back, ${firstName}! 👋`;
    const subMessage = isNewUser
        ? 'Your account has been created successfully.'
        : 'Great to see you again!';

    // Check if tour was dismissed previously
    useEffect(() => {
        const tourDismissed = localStorage.getItem('tour_dismissed');
        if (tourDismissed === 'true') {
            setShowTour(false);
        }

        // Trigger fade-in animation
        const timer = setTimeout(() => setIsVisible(true), 50);
        return () => clearTimeout(timer);
    }, []);

    const dismissTour = () => {
        localStorage.setItem('tour_dismissed', 'true');
        setShowTour(false);
    };

    return (
        <div className="min-h-screen bg-[#07090e] text-slate-100 font-body antialiased relative overflow-x-hidden flex flex-col selection:bg-brand-amber/30 selection:text-white">

            {/* Background Gradients */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[350px] bg-gradient-to-b from-emerald-500/5 to-transparent blur-[120px] pointer-events-none" />
            <div className="absolute bottom-0 right-[-10%] w-[500px] h-[500px] bg-brand-amber/[0.02] blur-[130px] pointer-events-none rounded-full" />

            <Navbar />

            <div className="flex-1 flex items-center justify-center px-6 py-20 md:py-28 relative z-10">
                <div className="w-full max-w-[560px]">

                    {/* Main Card */}
                    <div
                        className={`bg-white/[0.02] border border-white/[0.06] rounded-2xl p-8 md:p-12 backdrop-blur-sm shadow-2xl transition-all duration-700 ease-out ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
                            }`}
                    >

                        {/* Checkmark Animation - Different icon for new vs existing */}
                        <div className="flex justify-center mb-6">
                            <div className={`w-20 h-20 rounded-full border flex items-center justify-center animate-[bounceIn_0.6s_ease-out]
                                ${isNewUser
                                    ? 'bg-emerald-500/10 border-emerald-500/30'
                                    : 'bg-brand-amber/10 border-brand-amber/30'
                                }`}
                            >
                                {isNewUser ? (
                                    <svg
                                        className="w-10 h-10 text-emerald-400"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="3"
                                        viewBox="0 0 24 24"
                                    >
                                        <polyline
                                            points="20 6 9 17 4 12"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                ) : (
                                    <svg
                                        className="w-10 h-10 text-brand-amber"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                )}
                            </div>
                        </div>

                        {/* Welcome Text */}
                        <h1 className="text-3xl md:text-4xl font-display font-bold text-white text-center mb-2">
                            {greeting}
                        </h1>
                        <p className="text-slate-400 text-center font-light text-sm md:text-base mb-8">
                            {subMessage}
                        </p>

                        {/* Onboarding Tour - Only show for new users */}
                        {showTour && isNewUser && (
                            <div className="relative bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 mb-8">
                                <button
                                    onClick={dismissTour}
                                    className="absolute top-3 right-3 text-slate-500 hover:text-slate-300 transition-colors"
                                    aria-label="Dismiss tour"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>

                                <h3 className="text-xs font-medium text-brand-amber uppercase tracking-widest mb-3">
                                    Quick Tour
                                </h3>

                                <div className="space-y-3">
                                    <div className="flex items-start gap-3">
                                        <span className="text-lg">📊</span>
                                        <div>
                                            <p className="text-sm font-medium text-white">Analyze</p>
                                            <p className="text-xs text-slate-400 font-light">
                                                Paste your message and get instant risk analysis
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <span className="text-lg">📈</span>
                                        <div>
                                            <p className="text-sm font-medium text-white">Plans</p>
                                            <p className="text-xs text-slate-400 font-light">
                                                View plans and pricing
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4">
                            <button
                                onClick={() => navigate('/plans')}
                                className="flex-1 px-6 py-3 border border-brand-amber/60 text-brand-amber hover:bg-brand-amber/10 rounded-lg font-medium transition-all duration-200 text-center"
                            >
                                Go to Plans
                            </button>
                            <button
                                onClick={() => navigate('/analyze')}
                                className="flex-1 px-6 py-3 border border-brand-amber/60 text-brand-amber hover:bg-brand-amber/10 rounded-lg font-medium transition-all duration-200 text-center"
                            >
                                Go to Analyze
                            </button>
                        </div>

                    </div>
                </div>
            </div>

            <Footer />

            {/* Tailwind animation keyframes */}
            <style>{`
                @keyframes bounceIn {
                    0% { transform: scale(0.8); opacity: 0; }
                    50% { transform: scale(1.05); opacity: 1; }
                    70% { transform: scale(0.95); }
                    100% { transform: scale(1); opacity: 1; }
                }
            `}</style>
        </div>
    );
}